import { createContext, useContext, useState, ReactNode } from 'react'
import { useProductDetails } from '../hooks/useProductDetails'
import { useLogisticInventory } from '../hooks/useLogisticInventory'
import { ProductDetailsResponse, InventoryItem } from '../interfaces/inventory'

interface ProductDetailsContextType {
  selectedProductId: number | null
  setSelectedProductId: (_productId: number | null) => void
  productDetails: ProductDetailsResponse | undefined
  productMainInfo: InventoryItem | null | undefined
  isLoadingDetails: boolean
  isErrorDetails: boolean
  refetchDetails: () => void
}

const ProductDetailsContext = createContext<
  ProductDetailsContextType | undefined
>(undefined)

interface ProductDetailsProviderProps {
  children: ReactNode
}

export function ProductDetailsProvider({
  children,
}: ProductDetailsProviderProps) {
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null
  )

  const {
    data: productDetails,
    isLoading: isLoadingDetails,
    isError: isErrorDetails,
    refetch: refetchDetails,
  } = useProductDetails({
    productId: selectedProductId,
    enabled: false,
  })

  // Get main inventory data from the main context instead of making a new API call
  const { inventoryData } = useLogisticInventory()

  // Find the product in the already loaded inventory data
  const productMainInfo = selectedProductId
    ? inventoryData.find((item) => item.productId === selectedProductId)
    : null

  const isLoadingMainInfo = false // No loading since we use already loaded data

  return (
    <ProductDetailsContext.Provider
      value={{
        selectedProductId,
        setSelectedProductId,
        productDetails,
        productMainInfo,
        isLoadingDetails: isLoadingDetails || isLoadingMainInfo,
        isErrorDetails,
        refetchDetails,
      }}
    >
      {children}
    </ProductDetailsContext.Provider>
  )
}

export function useProductDetailsContext() {
  const context = useContext(ProductDetailsContext)
  if (context === undefined) {
    throw new Error(
      'useProductDetailsContext must be used within a ProductDetailsProvider'
    )
  }
  return context
}
